import React, { useState } from 'react';
import { toast } from 'sonner';
import { KeyRound, Mail, Phone, Loader2 } from 'lucide-react';
import api, { formatApiError } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

export function AccountSettings({ testIdPrefix = 'account' }) {
  const { user, updateUser } = useAuth();
  const [tab, setTab] = useState('email');
  const input = "h-10 w-full rounded-xl border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood";

  // email form
  const [newEmail, setNewEmail] = useState('');
  const [emailPw, setEmailPw] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailOtpSent, setEmailOtpSent] = useState(false);

  // password form
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [pwOtp, setPwOtp] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwOtpSent, setPwOtpSent] = useState(false);

  const sendOtp = async (which) => {
    try {
      await api.post('/account/send-otp', { purpose: 'account-change' });
      if (which === 'email') setEmailOtpSent(true); else setPwOtpSent(true);
      toast.success('Code sent to your phone', { description: `Check SMS on ${user?.phone}.` });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submitEmail = async () => {
    if (!newEmail.trim()) return toast.error('Enter your new email address');
    if (!emailPw && !emailOtp) return toast.error('Verify with your current password or the phone code');
    setEmailBusy(true);
    try {
      const res = await api.post('/account/change-email', { newEmail: newEmail.trim(), currentPassword: emailPw, otpCode: emailOtp });
      updateUser({ email: res.data.email });
      toast.success('Email updated', { description: `Your login email is now ${res.data.email}. Use it to log in next time.` });
      setNewEmail(''); setEmailPw(''); setEmailOtp(''); setEmailOtpSent(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setEmailBusy(false); }
  };

  const submitPassword = async () => {
    if (!newPw || newPw.length < 6) return toast.error('New password must be at least 6 characters');
    if (!curPw && !pwOtp) return toast.error('Verify with your current password or the phone code');
    setPwBusy(true);
    try {
      await api.post('/account/change-password', { currentPassword: curPw, newPassword: newPw, otpCode: pwOtp });
      toast.success('Password updated', { description: 'Use your new password next time you log in.' });
      setCurPw(''); setNewPw(''); setPwOtp(''); setPwOtpSent(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setPwBusy(false); }
  };

  return (
    <div data-testid={`${testIdPrefix}-settings`} className="space-y-4">
      <div className="flex gap-2">
        <button data-testid={`${testIdPrefix}-tab-email`} onClick={() => setTab('email')}
          className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${tab === 'email' ? 'border-kelate-darkwood bg-kelate-darkwood text-kelate-surface' : 'border-kelate-line bg-kelate-surface text-kelate-soft hover:border-kelate-wood'}`}>
          <Mail size={14} /> Email
        </button>
        <button data-testid={`${testIdPrefix}-tab-password`} onClick={() => setTab('password')}
          className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${tab === 'password' ? 'border-kelate-darkwood bg-kelate-darkwood text-kelate-surface' : 'border-kelate-line bg-kelate-surface text-kelate-soft hover:border-kelate-wood'}`}>
          <KeyRound size={14} /> Password
        </button>
      </div>

      <div className="rounded-xl border border-kelate-line/60 bg-secondary/30 px-4 py-3 text-xs text-kelate-soft">
        Signed in as <b className="text-kelate-ink">{user?.email}</b>
        {user?.phone && <> · Phone <b className="text-kelate-ink">{user.phone}</b></>}
      </div>

      {tab === 'email' && (
        <div className="space-y-3" data-testid={`${testIdPrefix}-email-form`}>
          <div>
            <label className="mb-1 block text-xs font-medium text-kelate-ink">New email address</label>
            <input data-testid={`${testIdPrefix}-new-email`} type="email" placeholder="you@example.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className={input} />
          </div>
          <div className="rounded-xl border border-kelate-line/60 p-3">
            <p className="mb-2 text-xs font-medium text-kelate-ink">Verify it's you — choose one:</p>
            <div className="space-y-2">
              <input data-testid={`${testIdPrefix}-email-current-password`} type="password" placeholder="Current password" value={emailPw} onChange={(e) => { setEmailPw(e.target.value); if (e.target.value) setEmailOtp(''); }} className={input} />
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-kelate-soft">or</span>
                {user?.phone ? (
                  emailOtpSent ? (
                    <input data-testid={`${testIdPrefix}-email-otp`} inputMode="numeric" placeholder="6-digit SMS code" value={emailOtp} onChange={(e) => { setEmailOtp(e.target.value); if (e.target.value) setEmailPw(''); }} className={`${input} max-w-[180px]`} />
                  ) : (
                    <button type="button" data-testid={`${testIdPrefix}-email-send-otp`} onClick={() => sendOtp('email')} className="flex items-center gap-1.5 rounded-lg border border-kelate-line px-3 py-2 text-xs text-kelate-darkwood hover:border-kelate-wood">
                      <Phone size={12} /> Send code to {user.phone}
                    </button>
                  )
                ) : (
                  <span className="text-[11px] text-kelate-soft">(no phone on account)</span>
                )}
              </div>
            </div>
          </div>
          <button data-testid={`${testIdPrefix}-email-submit`} onClick={submitEmail} disabled={emailBusy} className="flex items-center gap-2 rounded-xl bg-kelate-darkwood px-5 py-2.5 text-sm font-medium text-kelate-surface hover:bg-kelate-wood disabled:opacity-50">
            {emailBusy && <Loader2 size={14} className="animate-spin" />} Update Email
          </button>
        </div>
      )}

      {tab === 'password' && (
        <div className="space-y-3" data-testid={`${testIdPrefix}-password-form`}>
          <div>
            <label className="mb-1 block text-xs font-medium text-kelate-ink">New password</label>
            <input data-testid={`${testIdPrefix}-new-password`} type="password" placeholder="At least 6 characters" value={newPw} onChange={(e) => setNewPw(e.target.value)} className={input} />
          </div>
          <div className="rounded-xl border border-kelate-line/60 p-3">
            <p className="mb-2 text-xs font-medium text-kelate-ink">Verify it's you — choose one:</p>
            <div className="space-y-2">
              <input data-testid={`${testIdPrefix}-pw-current-password`} type="password" placeholder="Current password" value={curPw} onChange={(e) => { setCurPw(e.target.value); if (e.target.value) setPwOtp(''); }} className={input} />
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-kelate-soft">or</span>
                {user?.phone ? (
                  pwOtpSent ? (
                    <input data-testid={`${testIdPrefix}-pw-otp`} inputMode="numeric" placeholder="6-digit SMS code" value={pwOtp} onChange={(e) => { setPwOtp(e.target.value); if (e.target.value) setCurPw(''); }} className={`${input} max-w-[180px]`} />
                  ) : (
                    <button type="button" data-testid={`${testIdPrefix}-pw-send-otp`} onClick={() => sendOtp('pw')} className="flex items-center gap-1.5 rounded-lg border border-kelate-line px-3 py-2 text-xs text-kelate-darkwood hover:border-kelate-wood">
                      <Phone size={12} /> Send code to {user.phone}
                    </button>
                  )
                ) : (
                  <span className="text-[11px] text-kelate-soft">(no phone on account)</span>
                )}
              </div>
            </div>
          </div>
          <button data-testid={`${testIdPrefix}-pw-submit`} onClick={submitPassword} disabled={pwBusy} className="flex items-center gap-2 rounded-xl bg-kelate-darkwood px-5 py-2.5 text-sm font-medium text-kelate-surface hover:bg-kelate-wood disabled:opacity-50">
            {pwBusy && <Loader2 size={14} className="animate-spin" />} Update Password
          </button>
        </div>
      )}
    </div>
  );
}
